import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { Box, IconButton, Stack } from '@mui/material';
import { useForm } from 'react-hook-form';

import _ from 'lodash';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { PrimaryOutlinedButton } from '../../../components/style/ButtonStyle';
import { CommonDivider } from '../../../components/style/CommonStyle';
import {
  DetailInfoText,
  FormLabelText,
  FormTitle,
  MobileTitle,
} from '../../../components/style/TextStyle';
import { useAuthAxios } from '../../../hooks/useAuthAxios';
import useResponsive from '../../../hooks/useResponsive';
import { useYupValidationResolver } from '../../../hooks/useYupValidationResolver';
import { responsiveMargin } from '../../../utils/enum';
import { TextInput } from '../MapPage/FacilityFormPage/CommonForm';
import { defaultValue, userSchema } from './Common/DefaultAndSchema';
import { EditUserButton } from './Common/EditUserButton';
import UserInputs from './Common/UserInputs';

export default function EditUserPage() {
  const mdUp = useResponsive('up', 'md');
  const { userId } = useParams();

  const [getUserResult] = useAuthAxios(
    {
      method: 'GET',
      url: `/api/v1/users/${userId}`,
    },
    { manual: false },
  );

  const user = _.get(getUserResult, 'response.data');

  const resolver = useYupValidationResolver(
    Yup.object().shape({
      ...userSchema,
    }),
  );

  const form = useForm<any>({
    mode: 'onTouched',
    resolver,
    defaultValues: {
      ...defaultValue,
    },
    shouldUnregister: false,
  });

  const setValue = form.setValue;
  useEffect(() => {
    if (user) {
      setValue('administrativeRegion', user?.administrativeRegion);
      setValue('departmentId', user?.departmentId);
      setValue('email', user?.email);
      setValue('name', user?.name);
      setValue('phone', user?.phone);
      setValue('role', user?.role);
      setValue('createdAt', user?.createdAt);
      setValue('userId', user?.userId);
    }
  }, [user, setValue]);

  const [putUserResult, putUser] = useAuthAxios({
    method: 'PUT',
    url: `/api/v1/user/${userId}`,
  });

  const onSubmit = form.handleSubmit(function (values) {
    const payload = {
      administrativeRegion: values.administrativeRegion,
      createdAt: values.createdAt,
      departmentId: values.departmentId,
      email: values.email,
      id: values.id,
      name: values.name,
      phone: values.phone,
      role: values.role,
      userId: values.userId,
    };
    putUser({ data: payload });
  });

  return (
    <Stack mx={responsiveMargin} spacing={5} my={{ xs: 2, md: 10 }}>
      {mdUp ? (
        <DesktopHeader
          userId={userId}
          putUserResult={putUserResult}
          onSubmit={onSubmit}
        />
      ) : (
        <MobileHeader
          userId={userId}
          putUserResult={putUserResult}
          onSubmit={onSubmit}
        />
      )}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        sx={{ w: '100%' }}
        spacing={5}
      >
        <Stack sx={{ flex: 1 }}>
          <FormLabelText>
            ID<span>*</span>
          </FormLabelText>
          <DetailInfoText>{user?.id || '-'}</DetailInfoText>
        </Stack>
        <Stack sx={{ flex: 1 }}>
          <TextInput
            form={form}
            isRequired={true}
            label="이름"
            fieldPath="name"
            placeholder="예시 : 홍길동"
          />
        </Stack>
      </Stack>
      <UserInputs form={form} />
    </Stack>
  );
}

function DesktopHeader({ putUserResult, onSubmit, userId }: any) {
  const navigate = useNavigate();
  return (
    <>
      <IconButton
        sx={{ width: 'fit-content', justifyContent: 'flex-start' }}
        onClick={() => navigate(`/users`)}
      >
        <KeyboardArrowLeftIcon />
      </IconButton>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <FormTitle>이용자 정보수정</FormTitle>
        <Box />
        <Stack direction="row" spacing={1}>
          <PrimaryOutlinedButton
            sx={{ width: 130, height: 40 }}
            onClick={() => {
              navigate(`/users/${userId}`);
            }}
          >
            취소
          </PrimaryOutlinedButton>
          <EditUserButton
            isDesktop={true}
            onSubmit={onSubmit}
            putUserResult={putUserResult}
            userId={userId}
          />
        </Stack>
      </Stack>
      <CommonDivider />
    </>
  );
}

function MobileHeader({ putUserResult, onSubmit, userId }: any) {
  const navigate = useNavigate();
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <IconButton
        sx={{ width: 'fit-content', justifyContent: 'flex-start' }}
        onClick={() => navigate(`/users/${userId}`)}
      >
        <KeyboardArrowLeftIcon />
      </IconButton>
      <MobileTitle>이용자 정보수정</MobileTitle>
      <EditUserButton
        isDesktop={false}
        onSubmit={onSubmit}
        putUserResult={putUserResult}
        userId={userId}
      />
    </Stack>
  );
}
